import { createAsyncThunk } from '@reduxjs/toolkit';
import { pushToast } from '../reportingSlice';
import { DateTime } from 'luxon';
import { ReportConfig, ChartType } from 'src/types/reportingTypes';
import FileDownload from 'js-file-download';
import { RootState } from '../../../redux/store';

export type ExportReportAsPDFResult = {
	accountId?: string;
	name?: string;
	config?: ReportConfig;
	zoneId?: string;
	activeGraph?: string;
	activeReportTitle?: string;
};

export const exportReportAsPDF = createAsyncThunk<
	void,
	ExportReportAsPDFPayload,
	{ rejectValue: string; state: RootState }
>(
	'reporting/exportReportAsPDF',
	async (payload, { dispatch, rejectWithValue, getState }) => {
		const { api, accountId, config, zoneId, name, activeReportTitle } =
			payload;
		const state = getState();
		const activeGraph = state.reporting.activeGraph;
		const { absoluteDateRangeStart, absoluteDateRangeEnd } = config;
		const formattedStartDate = absoluteDateRangeStart
			? DateTime.fromISO(absoluteDateRangeStart, { setZone: true })
					.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS")
			: null;

		const formattedEndDate = absoluteDateRangeEnd
			? DateTime.fromISO(absoluteDateRangeEnd, { setZone: true })
					.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS")
			: null;

		const updatedChartConfig = {
			...config,
			chartType: activeGraph as ChartType,
			absoluteDateRangeStart: formattedStartDate,
			absoluteDateRangeEnd: formattedEndDate,
			groupBy: config.groupBy || [],
			sortBy: config.sortBy || [],
			filters: config.filters || [],
			topDisplayCount: config.topDisplayCount || 5,
			reportName: activeReportTitle,
		};
		const safeName = name && name.trim() ? name : 'report';
		try {
			const result = await api.exportReportAsPDF(
				accountId,
				updatedChartConfig,
				zoneId,
			);
			FileDownload(result, `${safeName}.pdf`);
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred';
			dispatch(
				pushToast({
					type: 'error',
					message: 'Error exporting report. Please try again later.',
				}),
			);
			return rejectWithValue(message);
		}
	},
);

type ExportReportAsPDFPayload = {
	api: {
		exportReportAsPDF: (
			accountId: string,
			config: ReportConfig,
			zoneId: string,
		) => Promise<string>;
	};
	accountId: string;
	config: ReportConfig;
	zoneId: string;
	name: string;
	activeReportTitle: string;
};
